import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Row, RowData } from '@vendasta/galaxy/table';
import { CrmFieldService, StandardExternalIds, StandardIds, SystemFieldIds } from '../../../shared-services';
import { CrmInjectionToken, ObjectType } from '../../../tokens-and-interfaces';
import { format, ParsedNumber, parseNumber } from 'libphonenumber-js';
import { GalaxyCurrencyPipe, GalaxyDatePipe } from '@vendasta/galaxy/pipes';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { FieldValueInterface } from '@vendasta/crm';
import { PrimaryAssociationCellComponent } from '../../custom-table-cells';

interface DisplayComponent {
  component?: any;
  inputs?: any;
}

const asString = (value: unknown): string => {
  if (!value) {
    return '';
  }
  if (isFieldValueInterface(value)) {
    return asString(value[0]?.stringValue);
  }
  return String(value);
};

const isFieldValueInterface = (value: unknown): value is FieldValueInterface[] => {
  return Array.isArray(value);
};

@Component({
  selector: 'crm-list-objects-board-card-content',
  templateUrl: './card-content.component.html',
  styleUrls: ['./card-content.component.scss'],
  imports: [CommonModule, GalaxyLoadingSpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListObjectsBoardCardContentComponent {
  private readonly userService = inject(CrmInjectionToken).services?.userService;

  readonly objectType = input.required<ObjectType>();
  readonly row = input.required<Row>();

  private readonly crmFieldService = inject(CrmFieldService);
  private readonly fieldIDs = {
    FirstName: this.crmFieldService.getFieldId(StandardExternalIds.FirstName),
    LastName: this.crmFieldService.getFieldId(StandardExternalIds.LastName),
    PhoneNumber: this.crmFieldService.getFieldId(StandardExternalIds.PhoneNumber),
    Email: this.crmFieldService.getFieldId(StandardExternalIds.Email),
  };

  protected readonly title = computed(() => this.computeTitle());
  protected readonly subtitle = computed(() => this.computeSubtitle());
  protected readonly details = computed(() => this.computeDetails());
  protected readonly bottomLeft = computed(() => this.computeBottomLeft());

  protected readonly bottomRight = signal('');
  protected readonly isLoadingBottomRight = signal(false);
  protected readonly ownerId = computed(() => this.computeOwnerId());
  protected readonly showFooter = computed(() => this.bottomLeft().length > 0 || this.ownerId());

  constructor(private readonly translate: TranslateService) {
    effect(async () => {
      const ownerId = this.ownerId();
      if (ownerId && this.userService) {
        this.isLoadingBottomRight.set(true);
        const users = await firstValueFrom(this.userService.getMultiUsers([ownerId]));
        const ownerName = users?.[0]?.displayName || '';
        this.bottomRight.set(ownerName);
        this.isLoadingBottomRight.set(false);
      }
    });
  }

  private computeTitle(): string {
    const data = this.row()?.data;
    const objectType = this.objectType();
    if (objectType === 'Contact') {
      return this.getContactName(data);
    }
    if (objectType === 'Company') {
      return this.getCompanyName(data);
    }
    if (objectType === 'Opportunity') {
      return this.getOpportunityName(data);
    }
    if (objectType === 'Activity') {
      return asString(data?.[StandardIds.ActivityName]?.value?.name) || '';
    }
    return '--';
  }

  private computeSubtitle(): DisplayComponent {
    const objectId = this.row()?.id;
    const objectType = this.objectType();
    switch (objectType) {
      case 'Opportunity':
        return {
          component: PrimaryAssociationCellComponent,
          inputs: {
            value: {
              objectType: objectType,
              objectId: objectId,
              showUrl: false,
            },
          },
        };
    }
    return {};
  }

  private computeDetails(): string[] {
    const data = this.row()?.data;
    const objectType = this.objectType();
    switch (objectType) {
      case 'Contact':
        return this.getContactDetails(data);
      case 'Company':
        return this.getCompanyDetails(data);
      case 'Opportunity':
        return this.getOpportunityDetails(data);
      default:
        break;
    }
    return [];
  }

  private computeBottomLeft(): string {
    const data = this.row()?.data;
    const objectType = this.objectType();
    if (objectType === 'Opportunity') {
      return this.getOpportunityValue(data);
    }
    return '';
  }

  private computeOwnerId() {
    const data = this.row()?.data;
    const objectType = this.objectType();
    switch (objectType) {
      case 'Opportunity':
        return asString(data?.[SystemFieldIds.OpportunityOwnerID]?.value);
      default:
        break;
    }
    return '';
  }

  private getContactName(data?: RowData): string {
    const firstNameData = asString(data?.[this.fieldIDs.FirstName]?.value);
    const lastNameData = asString(data?.[this.fieldIDs.LastName]?.value);
    if (firstNameData || lastNameData) {
      return `${firstNameData} ${lastNameData}`.trim();
    }
    return '--';
  }

  private getContactDetails(data?: RowData): string[] {
    const phoneValue = asString(data?.[this.fieldIDs.PhoneNumber]?.value);
    const phoneNumber = phoneValue
      ? format(parseNumber(phoneValue, { defaultCountry: 'US' }) as ParsedNumber, 'INTERNATIONAL')
      : '';

    const details: string[] = [
      asString(data?.[this.fieldIDs.Email]?.value),
      asString(data?.[StandardIds.ContactJobTitle]?.value),
      phoneNumber,
      data?.[StandardIds.ContactLinkedInURL]?.value,
    ];
    return details.filter((d) => d?.length > 0);
  }

  private getCompanyName(data?: RowData): string {
    return asString(data?.[StandardIds.CompanyName]?.value) || '--';
  }

  private getCompanyDetails(data?: RowData): string[] {
    const phoneValue = asString(data?.[StandardIds.CompanyPhoneNumber]?.value);
    const phoneNumber = phoneValue
      ? format(parseNumber(phoneValue, { defaultCountry: 'US' }) as ParsedNumber, 'INTERNATIONAL')
      : '';

    const address = [
      asString(data?.[StandardIds.CompanyPrimaryAddressLine1]?.value),
      asString(data?.[StandardIds.CompanyPrimaryAddressLine2]?.value),
      asString(data?.[StandardIds.CompanyPrimaryAddressCity]?.value),
      asString(data?.[StandardIds.CompanyPrimaryAddressState]?.value),
      asString(data?.[StandardIds.CompanyPrimaryAddressPostalCode]?.value),
    ]
      .filter(Boolean)
      .join(', ');

    const details: string[] = [asString(data?.[StandardIds.CompanyWebsite]?.value), address, phoneNumber];
    return details.filter((d) => d?.length > 0);
  }

  private getOpportunityName(data?: RowData): string {
    return asString(data?.[StandardIds.OpportunityName]?.value) || '--';
  }

  private getOpportunityValue(data?: RowData): string {
    const currencyValue = data?.[StandardIds.OpportunityAmount];
    const amount = currencyValue?.value ?? 0;
    if (currencyValue?.cellType == 'currency' && currencyValue?.code) {
      return new GalaxyCurrencyPipe().transform(amount, currencyValue.code);
    }
    return '';
  }

  private getOpportunityDetails(data?: RowData): string[] {
    const details = [];
    if (data) {
      details.push(this.getObjectRowDetails(data, SystemFieldIds.OpportunityLastActivityDate));
      if (data[StandardIds.OpportunityActualCloseDate]?.value) {
        details.push(this.getObjectRowDetails(data, StandardIds.OpportunityActualCloseDate));
      } else {
        details.push(this.getObjectRowDetails(data, StandardIds.OpportunityExpectedCloseDate));
      }
    }
    return details.filter((d) => d?.length > 0);
  }

  private getObjectRowDetails(data: RowData, fieldId: string): string {
    const cellValue = data[fieldId]?.value;
    const cellType = data[fieldId]?.cellType;
    if (cellValue) {
      let formattedValue = '';
      switch (cellType) {
        case 'date':
          formattedValue = new GalaxyDatePipe().transform(cellValue);
          break;
        default:
          formattedValue = asString(cellValue);
          break;
      }
      const translationKey = FieldTranslationKeys[fieldId];
      return `${this.translate.instant(translationKey)}: ${formattedValue}`;
    }

    return '';
  }
}

const FieldTranslationKeys: { [name: string]: string } = {
  [SystemFieldIds.OpportunityLastActivityDate]: 'OPPORTUNITY.PIPELINE.LAST_ACTIVITY',
  [StandardIds.OpportunityExpectedCloseDate]: 'OPPORTUNITY.PIPELINE.EXPECTED_CLOSE_DATE',
  [StandardIds.OpportunityActualCloseDate]: 'OPPORTUNITY.PIPELINE.ACTUAL_CLOSE_DATE',
};
